import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Badge, useToast } from '../ui';
import {
  getBillingUsage,
  getBillingSubscription,
  getBillingPlans,
  createCheckoutSession,
  createBillingPortal,
} from '../../api';
import type { BillingUsage, BillingSubscription, SubscriptionPlan } from '../../types';
import { metaTrack, onceEver, planValue, planCurrency } from '../../metaPixel';
import './BillingSection.css';

interface BillingSectionProps {
  workspaceId: number;
}

/* ── helpers ── */

function formatPrice(cents: number, currency: string): string {
  if (cents === 0) return 'Free';
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
  }).format(dollars);
}

function fmtLimit(val: number | null | undefined | string): string {
  if (val === null || val === undefined || val === 'unlimited') return '∞';
  if (val === 0) return '—';
  return String(val);
}

function usagePercent(used: number, limit: number | null): number {
  if (limit === null || limit === 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function usageBarColor(pct: number): string {
  if (pct >= 90) return '#ef4444';
  if (pct >= 70) return '#f59e0b';
  return 'var(--color-primary-600)';
}

function formatAllowedClipSeconds(seconds?: number[]): string {
  if (!seconds || seconds.length === 0) return '';
  return seconds.map((value) => `${value}s`).join(', ');
}

function planHeadlineDescription(plan: SubscriptionPlan): string {
  if (plan.code === 'starter') {
    return '12 Instagram content ideas/briefs per month. Conistra tells you what to create, write, and post. You make the visuals yourself.';
  }
  if (plan.code === 'creator') {
    return 'First tier with publishable Instagram assets. Conistra generates captions plus ready-to-use media.';
  }
  return plan.description;
}

function primaryOutputLabel(planCode: string): string {
  return planCode === 'starter' ? '🧠 Ideas/briefs/month' : '📝 Publishable posts/month';
}

function primaryOutputMetricLabel(planCode: string): string {
  return planCode === 'starter' ? 'Ideas/Briefs Planned' : 'Publishable Posts Created';
}

function primaryOutputNudgeLabel(planCode: string): string {
  return planCode === 'starter' ? 'ideas/briefs' : 'publishable posts';
}

/* ── UsageMeter ── */

interface UsageMeterProps {
  label: string;
  used: number;
  limit: number | null;
  unit?: string;
  highlight?: boolean;
  detail?: string;
}

function UsageMeter({ label, used, limit, unit = '', highlight, detail }: UsageMeterProps) {
  const pct = usagePercent(used, limit);
  const isUnlimited = limit === null;
  const isUnavailable = limit === 0;

  return (
    <div className={`billing-meter${highlight ? ' billing-meter--highlight' : ''}`}>
      <div className="billing-meter__header">
        <span className="billing-meter__label">{label}</span>
        <span
          className={`billing-meter__counts${pct >= 90 ? ' billing-meter__counts--danger' : pct >= 70 ? ' billing-meter__counts--warn' : ''}`}
        >
          {isUnavailable ? (
            <span className="billing-meter__locked">🔒 Not included</span>
          ) : isUnlimited ? (
            <span className="billing-meter__unlimited">∞ Unlimited</span>
          ) : (
            <>
              <strong>{used.toLocaleString()}</strong>
              {unit && ` ${unit}`} / {(limit as number).toLocaleString()}
              {unit && ` ${unit}`}
            </>
          )}
        </span>
      </div>
      {!isUnavailable && !isUnlimited && (
        <div className="billing-meter__bar-bg">
          <div
            className="billing-meter__bar-fill"
            style={{ width: `${pct}%`, background: usageBarColor(pct) }}
          />
        </div>
      )}
      {pct >= 90 && !isUnavailable && !isUnlimited && (
        <p className="billing-meter__warning">
          ⚠️ Almost at limit — upgrade to avoid interruptions.
        </p>
      )}
      {detail && !isUnavailable && <p className="billing-meter__detail">{detail}</p>}
    </div>
  );
}

/* ── Feature list ── */

const FEATURE_LABELS: Record<string, string> = {
  content_generation: '✍️ Content generation',
  image_generation: '🖼️ Image generation',
  video_generation: '🎬 Video generation',
  auto_publish: '🚀 Auto-publish',
  autopilot: '🤖 Full autopilot',
  scheduler_access: '📅 Smart scheduler',
  analytics: '📊 Analytics',
  regeneration: '🔄 Content regeneration',
  strategy_generation: '🎯 Strategy generation',
  outline_generation: '📝 Outline generation',
};

const KEY_FEATURES_CARD = [
  'content_generation',
  'image_generation',
  'video_generation',
  'auto_publish',
  'autopilot',
  'analytics',
  'regeneration',
];

interface PlanFeatureListProps {
  features: Record<string, boolean>;
  formats?: string[];
  variant?: 'current' | 'card';
}

function PlanFeatureList({ features, formats = [], variant = 'current' }: PlanFeatureListProps) {
  const keys = variant === 'card' ? KEY_FEATURES_CARD : Object.keys(FEATURE_LABELS);
  return (
    <ul className={`billing-feature-list billing-feature-list--${variant}`}>
      {keys.map((key) => {
        const enabled = features[key] ?? false;
        if (!FEATURE_LABELS[key]) return null;
        return (
          <li
            key={key}
            className={`billing-feature-item${enabled ? ' billing-feature-item--on' : ' billing-feature-item--off'}`}
          >
            <span className="billing-feature-item__icon">{enabled ? '✓' : '×'}</span>
            {FEATURE_LABELS[key]}
          </li>
        );
      })}
      {formats.length > 0 && (
        <li className="billing-feature-item billing-feature-item--on">
          <span className="billing-feature-item__icon">✓</span>
          Formats: {formats.join(', ')}
        </li>
      )}
    </ul>
  );
}

/* ── UpgradePlansModal ── */

interface UpgradePlansModalProps {
  currentPlanCode: string;
  plans: SubscriptionPlan[];
  workspaceId: number;
  onClose: () => void;
}

function UpgradePlansModal({
  currentPlanCode,
  plans,
  workspaceId,
  onClose,
}: UpgradePlansModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState<string | null>(null);

  const handleUpgrade = async (planCode: string, stripeConfigured: boolean) => {
    if (!stripeConfigured) {
      toast('Payment not yet available for this plan. Contact support.', 'error');
      return;
    }
    setLoading(planCode);
    try {
      const origin = window.location.origin;
      const result = await createCheckoutSession({
        workspace_id: workspaceId,
        plan_code: planCode,
        success_url: `${origin}/settings?billing=success`,
        cancel_url: `${origin}/settings?billing=cancel`,
      });
      window.location.href = result.url;
    } catch (err: any) {
      toast(err?.message ?? 'Failed to start checkout.', 'error');
      setLoading(null);
    }
  };

  const sorted = [...plans].sort((a, b) => a.display_order - b.display_order);

  return createPortal(
    <div className="billing-modal-backdrop" onClick={onClose}>
      <div className="billing-modal" onClick={(e) => e.stopPropagation()}>
        <div className="billing-modal__header">
          <div>
            <h2 className="billing-modal__title">Choose Your Plan</h2>
            <p className="billing-modal__subtitle">
              Upgrade anytime — your usage resets immediately on the next billing cycle.
            </p>
          </div>
          <button className="billing-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="billing-modal__plans">
          {sorted.map((plan) => {
            const isCurrent = plan.code === currentPlanCode;
            const isPopular = plan.code === 'creator';
            const limits = plan.entitlements?.limits ?? {};
            const features = plan.entitlements?.features ?? {};
            const formats = plan.entitlements?.formats ?? [];

            return (
              <div
                key={plan.code}
                className={[
                  'billing-plan-card',
                  isCurrent ? 'billing-plan-card--current' : '',
                  isPopular ? 'billing-plan-card--popular' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {isPopular && (
                  <div className="billing-plan-card__popular-badge">⭐ Most Popular</div>
                )}
                {isCurrent && <div className="billing-plan-card__current-badge">Your Plan</div>}

                <div className="billing-plan-card__header">
                  <h3 className="billing-plan-card__name">{plan.name}</h3>
                  <div className="billing-plan-card__price">
                    <span className="billing-plan-card__price-amount">
                      {formatPrice(plan.price_cents, plan.currency)}
                    </span>
                    {plan.price_cents > 0 && (
                      <span className="billing-plan-card__price-period">
                        /{plan.billing_interval === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    )}
                  </div>
                  <p className="billing-plan-card__description">{planHeadlineDescription(plan)}</p>
                </div>

                <div className="billing-plan-card__limits">
                  <div className="billing-plan-card__limit-row">
                    <span>{primaryOutputLabel(plan.code)}</span>
                    <strong>{fmtLimit(limits.monthly_posts)}</strong>
                  </div>
                  <div className="billing-plan-card__limit-row">
                    <span>🖼️ Images/month</span>
                    <strong>{fmtLimit(limits.monthly_images)}</strong>
                  </div>
                  <div className="billing-plan-card__limit-row">
                    <span>🎬 Video seconds</span>
                    <strong>{fmtLimit(limits.monthly_video_seconds)}</strong>
                  </div>
                </div>

                <PlanFeatureList features={features} formats={formats} variant="card" />

                <div className="billing-plan-card__cta">
                  {isCurrent ? (
                    <Button variant="ghost" size="md" full disabled>
                      Current Plan
                    </Button>
                  ) : (
                    <Button
                      variant={isPopular ? 'primary' : 'secondary'}
                      size="md"
                      full
                      loading={loading === plan.code}
                      onClick={() => handleUpgrade(plan.code, plan.stripe_configured)}
                    >
                      {plan.price_cents === 0 ? 'Get Started' : `Upgrade to ${plan.name}`}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="billing-modal__footer">🔒 Secure payment via Stripe. Cancel anytime.</p>
      </div>
    </div>,
    document.body
  );
}

/* ── Main BillingSection ── */

export default function BillingSection({ workspaceId }: BillingSectionProps) {
  const { toast } = useToast();
  const [usage, setUsage] = useState<BillingUsage | null>(null);
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  /* Set when we come back from Stripe on an upgrade, cleared once the resulting
     subscription has loaded and the Purchase has been reported. */
  const pendingUpgradeRef = useRef(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  const load = useCallback(async () => {
    setLoadingData(true);
    try {
      const [usageData, subData, plansData] = await Promise.all([
        getBillingUsage(workspaceId),
        getBillingSubscription(workspaceId),
        getBillingPlans(),
      ]);
      setUsage(usageData);
      setSubscription(subData);
      setPlans(plansData);
    } catch {
      // billing section is non-critical; silent fail
    } finally {
      setLoadingData(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingStatus = params.get('billing');
    if (billingStatus === 'success') {
      toast('🎉 Subscription activated! Your plan has been upgraded.', 'success');
      // An in-app upgrade is revenue that never passes through /activate, so
      // without this the second-best conversion we have would be invisible to
      // Meta. The price is not known yet — `load()` is still in flight — so the
      // event is reported by the effect below, once the subscription lands.
      pendingUpgradeRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
    } else if (billingStatus === 'cancel') {
      toast('Checkout cancelled.', 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
    load();
  }, [load, toast]);

  useEffect(() => {
    if (!pendingUpgradeRef.current || !subscription) return;
    pendingUpgradeRef.current = false;

    // Free and past_due plans are not purchases. Reporting them would teach the
    // optimiser to buy the people who never complete a payment.
    const isPaid = subscription.status === 'active' || subscription.status === 'trialing';
    if (!isPaid || subscription.price_cents <= 0) return;

    // Keyed on the billing period so a genuine later upgrade still reports, but
    // a re-entry with the same period does not.
    const periodKey = subscription.current_period_start || 'unknown_period';
    onceEver(`upgrade_ws${workspaceId}_${subscription.plan_code}_${periodKey}`, () => {
      metaTrack('Purchase', {
        content_name: subscription.plan_code,
        content_type: 'product',
        content_category: 'subscription_upgrade',
        value: planValue(subscription.price_cents),
        currency: planCurrency(subscription.currency),
      });
    });
  }, [subscription, workspaceId]);

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const origin = window.location.origin;
      const result = await createBillingPortal({
        workspace_id: workspaceId,
        return_url: `${origin}/settings`,
      });
      window.location.href = result.url;
    } catch (err: any) {
      toast(err?.message ?? 'Failed to open billing portal.', 'error');
    } finally {
      setLoadingPortal(false);
    }
  };

  if (loadingData) {
    return (
      <div className="billing-section billing-section--loading">
        <div className="billing-skeleton" />
        <div className="billing-skeleton billing-skeleton--short" />
      </div>
    );
  }

  if (!usage || !subscription) return null;

  const features = subscription.entitlements?.features ?? {};
  const formats = subscription.entitlements?.formats ?? [];
  const limits = usage.limits;
  const usageData = usage.usage;
  const allowedVideoClipSeconds = formatAllowedClipSeconds(
    usage.video_generation?.allowed_seconds
  );
  const postsLimit = limits.monthly_posts;
  const postsUsed = usageData.posts_generated;
  const postsPct = usagePercent(postsUsed, postsLimit);
  const currentOutputLabel = primaryOutputNudgeLabel(subscription.plan_code);

  const currentPlan = plans.find((p) => p.code === subscription.plan_code);
  const nextPlan = plans
    .filter((p) => p.display_order > (currentPlan?.display_order ?? 0))
    .sort((a, b) => a.display_order - b.display_order)[0];

  const isAtOrNearPostLimit = postsPct >= 80;

  return (
    <>
      <div className="billing-section">
        {/* ── Current plan header ── */}
        <div className="billing-plan-header">
          <div className="billing-plan-header__left">
            <div className="billing-plan-header__title-row">
              <h3 className="billing-plan-header__name">{subscription.plan_name} Plan</h3>
              <Badge
                variant={
                  subscription.status === 'active'
                    ? 'approved'
                    : subscription.status === 'trialing'
                      ? 'draft'
                      : 'error'
                }
                dot
              >
                {subscription.status === 'active'
                  ? 'Active'
                  : subscription.status === 'trialing'
                    ? 'Trial'
                    : subscription.status}
              </Badge>
            </div>
            <p className="billing-plan-header__price">
              {formatPrice(subscription.price_cents, subscription.currency)}
              {subscription.price_cents > 0 && (
                <span className="billing-plan-header__interval">
                  /{subscription.billing_interval === 'monthly' ? 'month' : 'year'}
                </span>
              )}
            </p>
            {subscription.current_period_end && (
              <p className="billing-plan-header__period">
                Period ends{' '}
                {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
          </div>

          <div className="billing-plan-header__actions">
            <Button variant="secondary" size="sm" onClick={() => setShowUpgradeModal(true)}>
              View Plans
            </Button>
            {subscription.has_stripe_subscription && (
              <Button
                variant="ghost"
                size="sm"
                loading={loadingPortal}
                onClick={handleManageSubscription}
              >
                Manage Billing
              </Button>
            )}
          </div>
        </div>

        {/* ── Upgrade nudge (urgent) ── */}
        {isAtOrNearPostLimit && nextPlan && (
          <div className="billing-upgrade-nudge">
            <div className="billing-upgrade-nudge__content">
              <strong>Running low on {currentOutputLabel}!</strong>
              <span>
                You've used {postsUsed} of {postsLimit} {currentOutputLabel} this month. Upgrade to{' '}
                <strong>{nextPlan.name}</strong> for{' '}
                {fmtLimit(nextPlan.entitlements?.limits?.monthly_posts)}{' '}
                {primaryOutputNudgeLabel(nextPlan.code)}/month.
              </span>
            </div>
            <Button variant="primary" size="sm" onClick={() => setShowUpgradeModal(true)}>
              Upgrade Now →
            </Button>
          </div>
        )}

        {/* ── Usage meters ── */}
        <div className="billing-usage-section">
          <h4 className="billing-usage-section__title">Usage This Period</h4>
          <div className="billing-usage-grid">
            <UsageMeter
              label={primaryOutputMetricLabel(subscription.plan_code)}
              used={postsUsed}
              limit={postsLimit}
              highlight
            />
            <UsageMeter
              label="Images Generated"
              used={usageData.images_generated}
              limit={features.image_generation ? limits.monthly_images : 0}
            />
            <UsageMeter
              label="Video Seconds"
              used={usageData.video_seconds_generated}
              limit={features.video_generation ? limits.monthly_video_seconds : 0}
              unit="s"
              detail={allowedVideoClipSeconds ? `Clips: ${allowedVideoClipSeconds}` : undefined}
            />
            <UsageMeter
              label="AI Tokens"
              used={usageData.tokens_used}
              limit={limits.monthly_tokens}
            />
          </div>

          <p className="billing-usage-period">
            Resets on{' '}
            {usage.period.end
              ? new Date(usage.period.end).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                })
              : '—'}
          </p>
        </div>

        {/* ── Features list ── */}
        <div className="billing-features-section">
          <h4 className="billing-features-section__title">Included Features</h4>
          <PlanFeatureList features={features} formats={formats} variant="current" />
        </div>

        {/* ── Quiet upgrade nudge for non-max plans ── */}
        {nextPlan && !isAtOrNearPostLimit && (
          <div className="billing-upgrade-quiet">
            <span>
              Unlock more with <strong>{nextPlan.name}</strong> —{' '}
              {formatPrice(nextPlan.price_cents, nextPlan.currency)}/mo
            </span>
            <Button variant="accent" size="sm" onClick={() => setShowUpgradeModal(true)}>
              See Plans
            </Button>
          </div>
        )}
      </div>

      {showUpgradeModal && (
        <UpgradePlansModal
          currentPlanCode={subscription.plan_code}
          plans={plans}
          workspaceId={workspaceId}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </>
  );
}
